import { useMutation, useQuery, useQueryCache } from "react-query";
import {
  checkIfDepartmentCanBeDeleted,
  createDepartment,
  deleteDepartment,
  getDepartment,
  getDepartmentPermissions,
  getDepartments,
  updateDepartment,
  updateDepartmentPermissions,
  Department as CoreDepartment,
  Department,
} from "../queries/departments";
import useNetworkHeaders from "./useNetworkHeaders";
import { groupBy, mapValues } from "lodash";

const EMPLOYEES_LIST_QUERY_KEY = "employees" as const;

export default function useDepartments() {
  const { departmentsQuery, data } = useCoreDepartments();
  const { error, isLoading } = departmentsQuery;
  if (error) {
    return {
      data: [],
      isLoading: isLoading,
      dropdownOptions: [],
      error: error,
    };
  }

  const departments: Department[] = data || [];

  const dropdownOptions = departments.map((department) => {
    return {
      key: department.id,
      text: department.name,
      value: department.id,
    };
  });

  return { data: departments, isLoading, dropdownOptions };
}

export const DEPARTMENTS_LIST_QUERY_KEY = "coreDepartments" as const;
export const LEGACY_DEPARTMENTS_LIST_QUERY_KEY = "departments" as const;
export function useCoreDepartments({ onSuccess }: { onSuccess?: (data: CoreDepartment[]) => void } = {}) {
  const queryKey = DEPARTMENTS_LIST_QUERY_KEY;

  const networkHeaders = useNetworkHeaders();
  const departmentsQuery = useQuery(queryKey, getDepartments(networkHeaders), {
    onSuccess,
  });

  const getDepartmentsDescendingFrom = (rootId: number | string) => {
    const departments = departmentsQuery.data ?? [];
    const parentChildMap = mapValues(
      groupBy(departments.filter((d) => d.parent?.id), (d) => d.parent!.id),
      (children) => children.map((d) => d.id),
    );

    const parents: number[] = [Number(rootId)];
    const descendantIds: number[] = [];
    
    while (parents.length > 0) {
      const parentId = parents.shift()!;
      descendantIds.push(parentId);
      const children = parentChildMap[parentId] ?? [];
      parents.push(...children);
    }
    return descendantIds;
  };

  return { departmentsQuery, queryKey, data: departmentsQuery.data, getDepartmentsDescendingFrom };
}

export const DEPARTMENT_QUERY_KEY = "coreDepartment" as const;
export const buildDepartmentQueryKey = (id: number) => [DEPARTMENT_QUERY_KEY, `${id}`] as const;
export function useCoreDepartment(id?: number) {
  const networkHeaders = useNetworkHeaders();
  const departmentQuery = useQuery(buildDepartmentQueryKey(id!), () => getDepartment(networkHeaders)(id!), {
    enabled: !!id,
  });

  return { departmentQuery };
}

export function useUpdateCoreDepartment() {
  const networkHeaders = useNetworkHeaders();
  const queryCache = useQueryCache();
  const updateDepartmentMutation = useMutation(updateDepartment(networkHeaders), {
    onSuccess: ({ id }) => {
      queryCache.invalidateQueries(DEPARTMENTS_LIST_QUERY_KEY);
      queryCache.invalidateQueries(buildDepartmentQueryKey(id));
      queryCache.invalidateQueries(EMPLOYEES_LIST_QUERY_KEY);
      queryCache.invalidateQueries(LEGACY_DEPARTMENTS_LIST_QUERY_KEY);
    },
    throwOnError: true,
  });

  return updateDepartmentMutation;
}

export function useCreateCoreDepartment() {
  const networkHeaders = useNetworkHeaders();
  const queryCache = useQueryCache();
  const createDepartmentMutation = useMutation(createDepartment(networkHeaders), {
    onSuccess: () => {
      queryCache.invalidateQueries(DEPARTMENTS_LIST_QUERY_KEY);
      queryCache.invalidateQueries(EMPLOYEES_LIST_QUERY_KEY);
      queryCache.invalidateQueries(LEGACY_DEPARTMENTS_LIST_QUERY_KEY);
    },
    throwOnError: true,
  });

  return createDepartmentMutation;
}

export function useCheckDeleteDepartment() {
  const networkHeaders = useNetworkHeaders();
  const checkDeleteDepartment = async (id: number) => await checkIfDepartmentCanBeDeleted(networkHeaders)(id);
  return checkDeleteDepartment;
}

export function useDeleteDepartment() {
  const networkHeaders = useNetworkHeaders();
  const queryCache = useQueryCache();
  const deleteDepartmentMutation = useMutation(deleteDepartment(networkHeaders), {
    onSuccess: () => {
      queryCache.invalidateQueries(DEPARTMENTS_LIST_QUERY_KEY);
    },
    throwOnError: true,
  });

  return deleteDepartmentMutation;
}

export const DEPARTMENT_PERMISSIONS_QUERY_KEY = "coreDepartmentPermissions" as const;
export const buildDepartmentPermissionsQueryKey = (id: number) => [DEPARTMENT_PERMISSIONS_QUERY_KEY, `${id}`] as const;
export function useDepartmentPermissions(departmentId?: number | null) {
  const networkHeaders = useNetworkHeaders();
  const permissionsQuery = useQuery(
    buildDepartmentPermissionsQueryKey(departmentId!),
    () => getDepartmentPermissions(networkHeaders)(departmentId!),
    {
      enabled: !!departmentId,
    },
  );
  return { permissionsQuery };
}

export function useUpdateDepartmentPermissions() {
  const networkHeaders = useNetworkHeaders();
  const queryCache = useQueryCache();
  const updateDepartmentPermissionsMutation = useMutation(updateDepartmentPermissions(networkHeaders), {
    onSuccess: (_, { id }) => {
      queryCache.invalidateQueries(buildDepartmentPermissionsQueryKey(id));
    },
    throwOnError: true,
  });

  return updateDepartmentPermissionsMutation;
}
